import React, { useState, useEffect } from "react";
import { adSlotLocations } from "../utils/adBannerData";
import BannerPreview from "./BannerPreview";

export default function AdBannerForm({ banner, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    title: "",
    targetUrl: "",
    slotLocation: "",
    startDate: "",
    endDate: "",
    isActive: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (banner) {
      setFormData({
        title: banner.title || "",
        targetUrl: banner.target_url || "",
        slotLocation: banner.slot_location || "",
        startDate: banner.start_date || "",
        endDate: banner.end_date || "",
        isActive: banner.is_active ?? true,
      });
      if (banner.image) {
        setPreviewUrl(banner.image);
      }
    }
  }, [banner]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // --- FUNGSI BARU DITAMBAHKAN DI SINI ---
  const handleRemoveImage = () => {
    setImageFile(null); // 1. Hapus file dari state
    setPreviewUrl(banner ? banner.image : ""); // 2. Kembalikan preview ke gambar asli (jika ada) atau kosongkan
    document.getElementById("imageUpload").value = ""; // 3. Reset input file
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";

    if (!banner && !imageFile) {
      newErrors.imageUrl = "Banner image is required";
    }

    if (!formData.targetUrl.trim())
      newErrors.targetUrl = "Target URL is required";
    if (!formData.slotLocation)
      newErrors.slotLocation = "Banner location is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    if (!formData.endDate) newErrors.endDate = "End date is required";

    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) > new Date(formData.endDate)
    ) {
      newErrors.endDate = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const dataToSubmit = {
        ...formData,
        image: imageFile,
      };
      onSubmit(dataToSubmit);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
          {banner ? "Edit Banner Ad" : "Create New Banner Ad"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                {/* Title */}
                <div className="sm:col-span-6">
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Banner Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    id="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.title ? "border-red-300" : ""
                    }`}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                </div>

                {/* Image Upload */}
                <div className="sm:col-span-6">
                  <label
                    htmlFor="imageUpload"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Banner Image
                  </label>
                  <input
                    type="file"
                    name="imageUpload"
                    id="imageUpload"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />

                  {/* --- TOMBOL REMOVE DITAMBAHKAN DI SINI --- */}
                  {imageFile && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="mt-2 text-sm font-medium text-red-600 hover:text-red-800"
                    >
                      Remove Selected Image
                    </button>
                  )}

                  {/* --- PERBAIKAN: errors.image -> errors.imageUrl --- */}
                  {errors.imageUrl && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.imageUrl}
                    </p>
                  )}
                </div>

                {/* Target URL */}
                <div className="sm:col-span-6">
                  <label
                    htmlFor="targetUrl"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Target URL
                  </label>
                  <input
                    type="text"
                    name="targetUrl"
                    id="targetUrl"
                    value={formData.targetUrl}
                    onChange={handleChange}
                    placeholder="e.g. /subscription/premium"
                    className={`mt-1 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.targetUrl ? "border-red-300" : ""
                    }`}
                  />
                  {errors.targetUrl && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.targetUrl}
                    </p>
                  )}
                </div>

                {/* Banner Location */}
                <div className="sm:col-span-3">
                  <label
                    htmlFor="slotLocation"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Banner Location
                  </label>
                  <select
                    id="slotLocation"
                    name="slotLocation"
                    value={formData.slotLocation}
                    onChange={handleChange}
                    className={`mt-1 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.slotLocation ? "border-red-300" : ""
                    }`}
                  >
                    <option value="">Select location</option>
                    {adSlotLocations.map((slot) => (
                      <option key={slot.id} value={slot.id}>
                        {slot.name}
                      </option>
                    ))}
                  </select>
                  {errors.slotLocation && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.slotLocation}
                    </p>
                  )}
                </div>

                {/* Start Date */}
                <div className="sm:col-span-3">
                  <label
                    htmlFor="startDate"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    id="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className={`mt-1 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.startDate ? "border-red-300" : ""
                    }`}
                  />
                  {errors.startDate && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.startDate}
                    </p>
                  )}
                </div>

                {/* End Date */}
                <div className="sm:col-span-3">
                  <label
                    htmlFor="endDate"
                    className="block text-sm font-medium text-gray-700"
                  >
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    id="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className={`mt-1 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.endDate ? "border-red-300" : ""
                    }`}
                  />
                  {errors.endDate && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.endDate}
                    </p>
                  )}
                </div>

                {/* Active Status */}
                <div className="sm:col-span-3">
                  <div className="flex items-start pt-5">
                    <div className="flex items-center h-5">
                      <input
                        id="isActive"
                        name="isActive"
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={handleChange}
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label
                        htmlFor="isActive"
                        className="font-medium text-gray-700"
                      >
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="sm:col-span-6 flex justify-end space-x-3 mt-4">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {banner ? "Update Banner" : "Create Banner"}
                  </button>
                </div>
              </div>
            </form>
          </div>
          <div className="md:col-span-1">
            <BannerPreview
              banner={{ ...formData, image: previewUrl }}
              className="sticky top-6"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
